import produce from 'immer';
import { UnreachableCaseError } from 'ts-essentials';
import {
  IGridDropPosition,
  IGridPosition,
} from '../../../../components/DndGrid';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import { DropType } from '../../../../components/DndGrid/types';
import { MaybeNil } from '../../../../utils/interfaces';
import {
  DashboardColumnType,
  IAnalysisSummaryInfoFieldsFragment,
  IDashboardColumnFieldsFragment,
  IDashboardRowFieldsFragment,
  IExternalEmbedFieldsFragment,
  IImageEmbedFieldsFragment,
  IRichTextFieldsFragment,
} from '../../../../utils/apollo/graphqlSchema';
import isNil from 'lodash/isNil';
import { generateTransientRowId } from './generateTransientRowId';
import { DASHBOARD_COLUMNS_PER_ROW } from '../constants';

/**
 * This file contains set of functions (collectively called "grid updaters")
 * which provide a way to update the dashboard grid depending on the operation.
 * For example, adding new content requires adding a new row to the grid,
 * while move requires removing a column from source row and adding it to the
 * destination row.
 *
 * Each grid updater creates a IRowsProducer - a function which accepts existing
 * dashboard grid in a form of list of rows and produce the new rows depending on the
 * operation. This way the grid updater does not need the context of the
 * dashboard, it converts its arguments to a tailored function that performs the update
 *
 * Each IRowsProducer mustn't modify the rows, it must always produce new object
 * thus it uses Immer to do so
 */

export type IRowsProducer = (
  rows: IDashboardRowFieldsFragment[],
) => IDashboardRowFieldsFragment[];

type IDashboardContent =
  | {
      type: DashboardColumnType.RichText;
      richText: IRichTextFieldsFragment;
    }
  | {
      type: DashboardColumnType.Analysis;
      analysis: IAnalysisSummaryInfoFieldsFragment;
    }
  | {
      type: DashboardColumnType.ExternalEmbed;
      externalEmbed: IExternalEmbedFieldsFragment;
    }
  | {
      type: DashboardColumnType.ImageEmbed;
      imageEmbed: IImageEmbedFieldsFragment;
    };

type IDashboardAddContentArgs = {
  position?: MaybeNil<IGridDropPosition>;
} & IDashboardContent;

export const getRowsWithAddedContent =
  (payload: IDashboardAddContentArgs): IRowsProducer =>
  (rows) => {
    const output = produce(rows, (draft) => {
      let column: IDashboardColumnFieldsFragment;

      switch (payload.type) {
        case DashboardColumnType.Analysis: {
          column = {
            __typename: 'DashboardAnalysisColumn',
            colSpan: null,
            analysis: payload.analysis,
          };
          break;
        }
        case DashboardColumnType.RichText: {
          column = {
            __typename: 'DashboardRichTextColumn',
            colSpan: null,
            richText: payload.richText,
          };
          break;
        }
        case DashboardColumnType.ExternalEmbed: {
          column = {
            __typename: 'DashboardExternalEmbedColumn',
            colSpan: null,
            externalEmbed: payload.externalEmbed,
          };
          break;
        }
        case DashboardColumnType.ImageEmbed: {
          column = {
            __typename: 'DashboardImageEmbedColumn',
            colSpan: null,
            imageEmbed: payload.imageEmbed,
          };
          break;
        }
        default: {
          throw new UnreachableCaseError(payload);
        }
      }

      const position = payload?.position;

      if (isNil(position) || position.type === DropType.ROW) {
        // move the column to a brand-new row
        const rowIndex = position?.rowIndex ?? draft.length;
        draft.splice(rowIndex, 0, {
          __typename: 'DashboardRow',
          id: generateTransientRowId(),
          columns: [column],
          minHeight: null,
        });
      } else {
        draft[position.rowIndex].columns.splice(position.colIndex, 0, column);
      }
    });

    return getRowsWithNormalizedColSpans(output);
  };

type IDashboardUpdateContentArgs = IDashboardContent;

export const getRowsWithUpdatedContent =
  (payload: IDashboardUpdateContentArgs): IRowsProducer =>
  (rows) => {
    return produce(rows, (draft) => {
      const allColumns = draft.flatMap((row) => row.columns);
      for (const column of allColumns) {
        if (
          payload.type === DashboardColumnType.Analysis &&
          column.__typename === 'DashboardAnalysisColumn' &&
          column.analysis.id === payload.analysis.id
        ) {
          column.analysis = payload.analysis;
        } else if (
          payload.type === DashboardColumnType.RichText &&
          column.__typename === 'DashboardRichTextColumn' &&
          column.richText.id === payload.richText.id
        ) {
          column.richText = payload.richText;
        } else if (
          payload.type === DashboardColumnType.ExternalEmbed &&
          column.__typename === 'DashboardExternalEmbedColumn' &&
          column.externalEmbed.id === payload.externalEmbed.id
        ) {
          column.externalEmbed = payload.externalEmbed;
        } else if (
          payload.type === DashboardColumnType.ImageEmbed &&
          column.__typename === 'DashboardImageEmbedColumn' &&
          column.imageEmbed.id === payload.imageEmbed.id
        ) {
          column.imageEmbed = payload.imageEmbed;
        }
      }
    });
  };

type IDashboardRemovedContentArgs = IGridPosition;

export const getRowsWithRemovedContent =
  ({ rowIndex, colIndex }: IDashboardRemovedContentArgs): IRowsProducer =>
  (rows) => {
    const output = produce(rows, (draft) => {
      const row = draft[rowIndex];
      assertNotNil(row);
      assertCondition(colIndex < row.columns.length);
      row.columns.splice(colIndex, 1);
    });

    // if there are any empty rows, remove them
    return getRowsWithNormalizedColSpans(
      output.filter((row) => row.columns.length !== 0),
    );
  };

interface IDashboardChartMoveArgs {
  source: IGridPosition;
  destination: IGridDropPosition;
}

export const getRowsWithMovedContent =
  ({ source, destination }: IDashboardChartMoveArgs): IRowsProducer =>
  (rows) => {
    const output = produce(rows, (draft) => {
      const splicedAnalyses = draft[source.rowIndex].columns.splice(
        source.colIndex,
        1,
      );
      assertCondition(splicedAnalyses.length === 1);
      const movedColumn = splicedAnalyses[0];

      if (destination.type === DropType.ROW) {
        // move the analysis to a brand-new row
        draft.splice(destination.rowIndex, 0, {
          __typename: 'DashboardRow',
          id: generateTransientRowId(),
          columns: [
            produce(movedColumn, (columnDraft) => {
              columnDraft.colSpan = null;
            }),
          ],
          minHeight: null,
        });
      } else if (destination.type === DropType.COLUMN) {
        // move the analysis from one row to another

        // special case when rearranging analyses on the same row
        // if the source (moved analysis) was before the destination,
        // it means that destination index is no longer correct because the
        // array now contains one analysis less. -1 should correct that
        const destColIndex =
          source.rowIndex === destination.rowIndex &&
          source.colIndex < destination.colIndex
            ? destination.colIndex - 1
            : destination.colIndex;
        draft[destination.rowIndex].columns.splice(
          destColIndex,
          0,
          movedColumn,
        );
      } else {
        throw new UnreachableCaseError(destination);
      }
    });

    // if there are any empty rows, remove them
    return getRowsWithNormalizedColSpans(
      output.filter((row) => row.columns.length !== 0),
    );
  };

interface IDashboardUpdateColSpansArgs {
  rowIndex: number;
  newColSpan: number[];
}

export const getRowsWithUpdatedColSpan =
  ({ rowIndex, newColSpan }: IDashboardUpdateColSpansArgs): IRowsProducer =>
  (rows) => {
    return produce(rows, (draft) => {
      const row = draft[rowIndex];
      assertNotNil(row);
      assertCondition(newColSpan.length === row.columns.length);
      for (let i = 0; i < row.columns.length; i++) {
        row.columns[i].colSpan = newColSpan[i];
      }
    });
  };

interface IDashboardUpdateRowHeightsArgs {
  rowIndex: number;
  newMinHeight: number;
}

export const getRowsWithUpdatedRowHeights =
  ({ rowIndex, newMinHeight }: IDashboardUpdateRowHeightsArgs): IRowsProducer =>
  (rows) => {
    return produce(rows, (draft) => {
      const row = draft[rowIndex];
      assertNotNil(row);
      row.minHeight = newMinHeight;
    });
  };

export const getRowsWithNormalizedColSpans: IRowsProducer = (rows) =>
  produce(rows, (draft) => {
    for (const { columns } of draft) {
      const totalColSpans = columns.reduce((acc, { colSpan }) => {
        return acc + (colSpan ?? 0);
      }, 0);
      // blank column spans are ignore and the remaining columns may coincidentally add up to COLUMNS_PER_ROW
      // To avoid this, we need to reset the spans as well
      const hasBlankColSpan = columns.some((c) => isNil(c.colSpan));
      if (
        (totalColSpans !== DASHBOARD_COLUMNS_PER_ROW || hasBlankColSpan) &&
        columns.length !== 0
      ) {
        const newColSpan = DASHBOARD_COLUMNS_PER_ROW / columns.length;
        for (const column of columns) {
          column.colSpan = newColSpan;
        }
      }
    }
  });
